import {
    signInWithEmailAndPassword,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    reauthenticateWithCredential,
    EmailAuthProvider
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "./firebase";
import { evaluateTenantLifecycle } from "../utils/tenantLifecycle";

// Helper para carregar as últimas faturas do tenant bloqueado
const fetchLastInvoices = async (idTenant) => {
    try {
        const invoicesCol = collection(db, "publicCheckoutSessions");
        const q = query(
            invoicesCol,
            where("idTenant", "==", idTenant)
        );
        const snap = await getDocs(q);
        
        const mapped = snap.docs.map(d => {
            const data = d.data();
            let createdAtDate = null;
            if (data.createdAt) {
                if (typeof data.createdAt.toDate === "function") {
                    createdAtDate = data.createdAt.toDate();
                } else {
                    createdAtDate = new Date(data.createdAt);
                }
            }
            return {
                id: d.id,
                externalReference: data.externalReference || d.id,
                planCode: data.planCode || "",
                planTitle: data.planTitle || "",
                amount: data.transactionAmount || data.amount || 0,
                paymentStatus: data.paymentStatus || data.status || "unknown",
                paymentStatusDetail: data.paymentStatusDetail || "",
                paymentMethodId: data.paymentMethodId || data.selectedPaymentMethod || "",
                createdAt: createdAtDate ? createdAtDate.toISOString() : null,
                createdAtMs: createdAtDate ? createdAtDate.getTime() : 0,
                ticket_url: data.ticket_url || data.checkoutUrl || null,
                qr_code: data.qr_code || null,
                qr_code_base64: data.qr_code_base64 || null,
            };
        });

        // Ordena por data decrescente na memória e pega as últimas 5 faturas
        mapped.sort((a, b) => b.createdAtMs - a.createdAtMs);
        return mapped.slice(0, 5);
    } catch (e) {
        console.error("Erro ao carregar faturas no login block:", e);
        return [];
    }
};

/**
 * AuthService: Centraliza toda a lógica de autenticação e perfil do NeoApp.
 * NÃO armazena estado no localStorage. O estado é gerido pelo AuthContext via onAuthStateChanged.
 */
const AuthService = {
    /**
     * Realiza login simples por e-mail e senha
     */
    login: async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Busca o perfil detalhado no Firestore
            const profile = await AuthService.getUserProfile(user.uid);

            // Verifica o status do tenant (empresa)
            if (profile && profile.idTenant) {
                const tenantDocRef = doc(db, "tenants", profile.idTenant);
                const tenantSnap = await getDoc(tenantDocRef);
                if (tenantSnap.exists()) {
                    const tenantData = tenantSnap.data();
                    const lifecycle = evaluateTenantLifecycle(tenantData);

                    if (lifecycle.blocked) {
                        const invoices = (profile.role === "owner" || profile.role === "super_admin") ? await fetchLastInvoices(profile.idTenant) : [];
                        await signOut(auth);
                        const err = new Error(lifecycle.message || "Esta conta esta inativa ou com assinatura pendente. Reative sua conta para continuar.");
                        err.code = "tenant/inactive";
                        err.tenantId = profile.idTenant;
                        err.tenantName = tenantData.name || "Sua Empresa";
                        err.billingStatus = lifecycle.billingStatus || tenantData.billingStatus || "draft";
                        err.planId = tenantData.planId || null;
                        err.blockReason = lifecycle.reasonCode || "tenant_inactive";
                        err.trialStartDate = lifecycle.trialStartDate || null;
                        err.trialEndsAt = lifecycle.trialEndsAt || null;
                        err.role = profile.role || "employee";
                        err.invoices = invoices;
                        throw err;
                    }
                } else {
                    await signOut(auth);
                    const err = new Error("Esta conta esta inativa ou com assinatura pendente. Reative sua conta para continuar.");
                    err.code = "tenant/inactive";
                    err.tenantId = profile.idTenant;
                    err.tenantName = "Sua Empresa";
                    err.billingStatus = "draft";
                    err.planId = null;
                    err.role = profile.role || "employee";
                    err.invoices = [];
                    throw err;
                }
            }

            // Trava de segurança no Member-Side para impedir acessos bloqueados
            if (profile && profile.status === 'inactive') {
                await signOut(auth);
                throw new Error("Seu acesso foi bloqueado pelo administrador. Contate o suporte.");
            }

            return user;
        } catch (error) {
            console.error("Erro detalhado no login:", error);
            throw error;
        }
    },

    /**
     * Realiza logout completo do Firebase e limpa os dados locais
     */
    logout: async () => {
        try {
            await signOut(auth);
            return true;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Solicita código de recuperação de senha via Cloud Function
     */
    forgetPassword: async (email) => {
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const functions = getFunctions();
        const sendResetCode = httpsCallable(functions, "sendPasswordResetEmail");
        const result = await sendResetCode({ email });
        return result.data;
    },

    /**
     * Verifica o código e altera a senha via Cloud Function
     */
    verifyCodeAndChangePassword: async (email, code, newPassword) => {
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const functions = getFunctions();
        const verifyAndChange = httpsCallable(functions, "verifyResetCodeAndChangePassword");
        const result = await verifyAndChange({ email, code, newPassword });
        return result.data;
    },

    /**
     * Busca os dados do perfil na coleção global 'users'
     */
    getUserProfile: async (uid) => {
        try {
            const userDocRef = doc(db, "users", uid);
            const userSnap = await getDoc(userDocRef);
            if (userSnap.exists()) {
                return userSnap.data();
            }
            return null;
        } catch (error) {
            console.error("Erro ao buscar perfil:", error);
            return null;
        }
    },

    /**
     * Realiza login via provedores sociais (Google)
     */
    socialLogin: async (type) => {
        let provider;
        if (type === "google") {
            provider = new GoogleAuthProvider();
        }

        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Busca ou inicializa o perfil após login social
            let profile = await AuthService.getUserProfile(user.uid);

            // Se o perfil não existe, podemos inicializar um documento básico (Opcional)
            if (!profile) {
                const profileData = {
                    uid: user.uid,
                    fullName: user.displayName,
                    email: user.email,
                    role: 'employee', // Cargo padrão moderado
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };
                await setDoc(doc(db, "users", user.uid), profileData);
                profile = profileData;
            }

            // Verifica o status do tenant (empresa)
            if (profile && profile.idTenant) {
                const tenantDocRef = doc(db, "tenants", profile.idTenant);
                const tenantSnap = await getDoc(tenantDocRef);
                if (tenantSnap.exists()) {
                    const tenantData = tenantSnap.data();
                    const lifecycle = evaluateTenantLifecycle(tenantData);

                    if (lifecycle.blocked) {
                        const invoices = (profile.role === "owner" || profile.role === "super_admin") ? await fetchLastInvoices(profile.idTenant) : [];
                        await signOut(auth);
                        const err = new Error(lifecycle.message || "Esta conta esta inativa ou com assinatura pendente. Reative sua conta para continuar.");
                        err.code = "tenant/inactive";
                        err.tenantId = profile.idTenant;
                        err.tenantName = tenantData.name || "Sua Empresa";
                        err.billingStatus = lifecycle.billingStatus || tenantData.billingStatus || "draft";
                        err.planId = tenantData.planId || null;
                        err.blockReason = lifecycle.reasonCode || "tenant_inactive";
                        err.trialStartDate = lifecycle.trialStartDate || null;
                        err.trialEndsAt = lifecycle.trialEndsAt || null;
                        err.role = profile.role || "employee";
                        err.invoices = invoices;
                        throw err;
                    }
                } else {
                    await signOut(auth);
                    const err = new Error("Esta conta esta inativa ou com assinatura pendente. Reative sua conta para continuar.");
                    err.code = "tenant/inactive";
                    err.tenantId = profile.idTenant;
                    err.tenantName = "Sua Empresa";
                    err.billingStatus = "draft";
                    err.planId = null;
                    err.role = profile.role || "employee";
                    err.invoices = [];
                    throw err;
                }
            }

            // Trava de segurança no Member-Side para impedir acessos bloqueados
            if (profile && profile.status === 'inactive') {
                await signOut(auth);
                throw new Error("Seu acesso foi bloqueado pelo administrador. Contate o suporte.");
            }

            return user;
        } catch (error) {
            throw error;
        }
    },

    reauthenticate: async (password) => {
        const user = auth.currentUser;
        if (!user || !user.email) throw new Error("Usuário não autenticado.");
        const credential = EmailAuthProvider.credential(user.email, password);
        return reauthenticateWithCredential(user, credential);
    }
};

export default AuthService;
